/** What the supplier screens call things - **one home** for the words (design principle 7).
 *
 * Separate from the components so a fast-refresh boundary stays a boundary, and so the exchange
 * screen, the account page and the form cannot name one state two ways.
 */

import type { AllocationBasisName, Exchange } from "../api/suppliers";

const STATE_LABEL: Record<string, string> = {
  open: "Waiting for an answer",
  answered: "Answered",
  accepted: "In the inventory",
  declined: "Declined",
  withdrawn: "Withdrawn",
};

/** What each allocation basis is called to a supplier. **Exhaustive by type**: a `Record` over the
 * generated union, so a basis added on the server fails the typecheck here instead of appearing as
 * a raw enum value in a select. The hint says what the *whole* of that basis is, because the form
 * asks for the buyer's part and the whole, and "the whole of tonne-km" is not self-evident. */
export const BASIS_LABEL: Record<AllocationBasisName, { label: string; whole: string }> = {
  tonne_km: { label: "Tonne-kilometres", whole: "all tonne-kilometres you carried or delivered" },
  spend: { label: "Spend", whole: "everything every customer spent with you" },
  mass: { label: "Mass delivered", whole: "the total mass you delivered" },
  units: { label: "Units or deliveries", whole: "the total count of units or deliveries" },
  energy: { label: "Energy", whole: "the total energy delivered or consumed" },
  floor_area: { label: "Floor area", whole: "the total floor area of the site" },
  headcount: { label: "Headcount", whole: "the total number of people" },
};

export function stateLabel(state: string): string {
  return STATE_LABEL[state] ?? state;
}

/** The suppliers this workspace has asked before, in the order the exchange lists them, each once.
 * Only rows where this workspace is the buyer: on the other side the counterparty is a customer. */
export function knownSuppliers(rows: readonly Exchange[]): Array<{ id: string; asked: number }> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.i_am !== "buyer") continue;
    counts.set(row.supplier_org_id, (counts.get(row.supplier_org_id) ?? 0) + 1);
  }
  return [...counts].map(([id, asked]) => ({ id, asked }));
}

/** The suppliers this workspace has asked, numbered **in the order they were first asked**: `Supplier 1`
 * is the oldest. A supplier is another company's own workspace and this one holds no name for it
 * (ADR-0040; a name is ADR-0221, proposed), so the screen says what it is looking at and does not
 * print a fragment of an id (G-P7-12). The exchange lists newest first, so the pass is reversed: a new
 * request to a new supplier takes the next number and no older supplier is renumbered. Only rows
 * where this workspace is the buyer; the number belongs to the whole exchange, never to a filtered
 * list, or a row would change its name when a filter did. */
export function supplierNumbers(rows: readonly Exchange[]): Map<string, number> {
  const numbers = new Map<string, number>();
  for (const row of [...rows].reverse()) {
    if (row.i_am !== "buyer" || numbers.has(row.supplier_org_id)) continue;
    numbers.set(row.supplier_org_id, numbers.size + 1);
  }
  return numbers;
}

/** What a screen calls a supplier: its number, or the bare word when it is not in the list (a
 * supplier reached by a link before the exchange has loaded). Never an id. */
export function supplierName(id: string, numbers: ReadonlyMap<string, number>): string {
  const number = numbers.get(id);
  return number === undefined ? "Supplier" : `Supplier ${number}`;
}

/** The same for the customers that have asked this workspace for a figure, on the supplier's side. */
export function customerNumbers(rows: readonly Exchange[]): Map<string, number> {
  const numbers = new Map<string, number>();
  for (const row of [...rows].reverse()) {
    if (row.i_am !== "supplier" || numbers.has(row.buyer_org_id)) continue;
    numbers.set(row.buyer_org_id, numbers.size + 1);
  }
  return numbers;
}

export function customerName(id: string, numbers: ReadonlyMap<string, number>): string {
  const number = numbers.get(id);
  return number === undefined ? "Customer" : `Customer ${number}`;
}
